import { useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const inputCls = "w-full rounded-lg border border-stone-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand";

export const EnquiryForm = ({ property, locality, onDone, showIntent, defaultIntent = "buy" }) => {
  const [f, setF] = useState({ name: "", phone: "", email: "", message: "", intent: defaultIntent });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim()) { toast.error("Name and phone are required"); return; }
    setLoading(true);
    try {
      await api.post("/leads", {
        ...f,
        property_slug: property?.slug || "",
        property_title: property?.title || "",
        locality: locality || property?.locality || "",
      });
      toast.success("Thank you! We'll get back to you shortly.");
      setF({ name: "", phone: "", email: "", message: "", intent: defaultIntent });
      onDone?.();
    } catch (err) {
      toast.error("Could not send enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const msgPlaceholder = property
    ? "I'm interested in this property…"
    : f.intent === "sell"
    ? "Tell us about your property (location, type, size, expected price)"
    : "What are you looking for? (budget, area, type)";

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="enquiry-form">
      {showIntent && (
        <div className="flex gap-2" data-testid="enquiry-intent">
          {[{ v: "buy", l: "Buy" }, { v: "rent", l: "Rent" }, { v: "sell", l: "Sell" }].map((t) => (
            <button type="button" key={t.v} onClick={() => set("intent", t.v)} data-testid={`intent-${t.v}`}
              className={`flex-1 rounded-lg py-2 text-sm font-semibold border transition-colors ${
                f.intent === t.v ? "bg-brand text-white border-brand" : "border-stone-200 text-stone-600 hover:border-brand"}`}>
              {t.l}
            </button>
          ))}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name *" required
          className={inputCls} data-testid="enquiry-name" />
        <input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone number *" required
          className={inputCls} data-testid="enquiry-phone" />
      </div>
      <input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email (optional)" type="email"
        className={inputCls} data-testid="enquiry-email" />
      <textarea value={f.message} onChange={(e) => set("message", e.target.value)} rows={3}
        placeholder={msgPlaceholder}
        className={inputCls} data-testid="enquiry-message" />
      <button type="submit" disabled={loading} data-testid="enquiry-submit"
        className="w-full flex items-center justify-center gap-2 rounded-full bg-brand text-white py-3 text-sm font-semibold hover:bg-brand-dark transition-colors disabled:opacity-60">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {loading ? "Sending…" : "Send Enquiry"}
      </button>
    </form>
  );
};

export const EnquiryDialog = ({ open, onClose, property }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="enquiry-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md p-6 animate-fade-up">
        <button onClick={onClose} className="absolute top-4 right-4 text-stone-400 hover:text-stone-700" data-testid="enquiry-close"><X /></button>
        <h3 className="font-heading text-2xl text-stone-900 mb-1">Send an Enquiry</h3>
        <p className="text-sm text-stone-500 mb-5">{property ? `Regarding: ${property.title}` : "Tell us your requirement"}</p>
        <EnquiryForm property={property} onDone={onClose} />
      </div>
    </div>
  );
};
